import React, { useEffect, useState } from "react";
import { dashboardApi } from "../../api/dashboardApi";

type OperationalData = {
  lineConfig: { name: string; username: string; note: string };
  serverIPs: Array<{ address: string; family: string; internal: boolean }>;
  machines: Array<{
    id: string;
    name: string;
    provider: string;
    status: string;
    externalId: string | null;
    lastSeenAt: string | null;
    ip?: string;
  }>;
};

export default function OperationalNotesWidget() {
  const [data, setData] = useState<OperationalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    dashboardApi
      .getOperationalNotes()
      .then(setData)
      .catch((e) => {
        setErr(e?.message ?? "Errore caricamento");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card h-100">
        <div className="card-header fw-semibold">Note operative</div>
        <div className="card-body text-muted">
          <span className="spinner-border spinner-border-sm me-2" />
          Caricamento…
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="card h-100 border-warning">
        <div className="card-header fw-semibold">Note operative</div>
        <div className="card-body text-warning">{err}</div>
      </div>
    );
  }

  if (!data) return null;

  const { lineConfig, serverIPs, machines } = data;

  return (
    <div className="card h-100">
      <div className="card-header fw-semibold">Note operative</div>
      <div className="card-body">
        {/* Linea TIM */}
        <div className="mb-3">
          <div className="small fw-semibold text-primary mb-1">
            <i className="bi bi-wifi me-1" />
            Linea {lineConfig.name} ({lineConfig.username})
          </div>
          <div className="small text-muted">{lineConfig.note}</div>
        </div>

        {/* Indirizzi IP logici del server */}
        <div className="mb-3">
          <div className="small fw-semibold mb-1">
            <i className="bi bi-hdd-network me-1" />
            Indirizzi IP logici (server)
          </div>
          {serverIPs.length === 0 ? (
            <div className="small text-muted">—</div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {serverIPs.map((ip) => (
                <code key={ip.address} className="badge bg-light text-dark px-2 py-1">
                  {ip.address}
                </code>
              ))}
            </div>
          )}
        </div>

        {/* Macchine connesse */}
        <div>
          <div className="small fw-semibold mb-2">
            <i className="bi bi-cpu me-1" />
            Macchine connesse
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>Macchina</th>
                  <th>Provider</th>
                  <th>IP / ID</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => (
                  <tr key={m.id}>
                    <td className="fw-semibold">{m.name}</td>
                    <td>
                      <span className="badge bg-secondary">{m.provider}</span>
                    </td>
                    <td>
                      {m.ip ? (
                        <code className="small">{m.ip}</code>
                      ) : m.externalId ? (
                        <code className="small">{m.externalId}</code>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          m.status === "ONLINE"
                            ? "text-bg-success"
                            : m.status === "ALARM"
                              ? "text-bg-danger"
                              : "text-bg-secondary"
                        }`}
                      >
                        {m.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {machines.length === 0 && (
            <div className="small text-muted">Nessuna macchina registrata.</div>
          )}
        </div>
      </div>
    </div>
  );
}
